import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useContact } from "@/hooks/contact/use-contact";
import { Edit, Loader2 } from "lucide-react";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "@/api/v1-jwt/config/api";
import { useAuth } from "@/providers/auth";
import { updateContact } from "@/api/v1-jwt/contact/contact";
import { useQueryClient } from "@tanstack/react-query";

const routingValidation = {
  required: "Routing number is required",
  validate: (value) => {
    if (!/^\d+$/.test(value)) return "Routing number must be numeric";
    if (value.length !== 9) return "Routing number must be 9 digits";
  },
};

const accountValidation = {
  required: "Account number is required",
  validate: (value) =>
    /^\d{5,17}$/.test(value) || "Account number must be between 5-17 digits",
};

export const saveBankInfo = async ({ userId, contactId, nameOnAccount, values }) => {
  if (!userId) throw new Error("[SAVE_BANK_INFO] User ID missing.");
  if (!contactId) throw new Error("[SAVE_BANK_INFO] Contact ID missing.");

  const { data } = await api.post(
    `/platform/contacts/add_bank/${userId}/${contactId}`,
    {
      name_on_account: nameOnAccount,
      ach: {
        account_number: values.account,
        routing_number: values.routing,
        account_type: values.accountType,
      },
    }
  );
  return data;
};

const transferMoney = async (userId, payload) => {
  if (!userId) throw new Error("User ID Missing.");
  const { data, status } = await api.post(
    `/platform/transfers/move_money/third_party/${userId}`,
    payload
  );
  if (status !== 201) throw new Error(`Transfer failed with status ${status}`);
  return data;
};

function ContactInfo({ contact, accountType }) {
  const queryClient = useQueryClient();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId = userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const [editingName, setEditingName] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [nameError, setNameError] = useState("");

  const isBusiness = ["business_checking", "business_savings"].includes(accountType);
  const c = contact?.contact_info;
  const needsName = isBusiness
    ? !c?.business_name && !c?.business_dba
    : !c?.first_name || !c?.last_name;

  const displayName = isBusiness
    ? c?.business_dba || c?.business_name || ""
    : `${c?.first_name || ""} ${c?.last_name || ""}`.trim();

  const handleSave = () => {
    if (isBusiness && !businessName) {
      setNameError("Business Name is required");
      return;
    }
    if (!isBusiness && (!firstName || !lastName)) {
      setNameError("First and Last Names are required");
      return;
    }
    const payload = isBusiness
      ? { business_name: businessName }
      : { first_name: firstName, last_name: lastName };

    updateContact({ payload, contactId: contact?.contact_id, userId })
      .then(() => {
        queryClient.invalidateQueries(["contacts"]);
        toast.success("Contact updated");
        setEditingName(false);
      })
      .catch(() => toast.error("Failed to update contact."));
  };

  return (
    <div className="px-4 sm:grid sm:grid-cols-4 sm:gap-x-4 sm:px-0">
      <div className="col-span-4 flex justify-between items-start gap-x-3">
        <div className="space-y-4 flex-1">
          {!editingName ? (
            <>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label>{isBusiness ? "Business Name" : "Name"}</Label>
                <div className="text-sm leading-6 text-slate-700">{displayName}</div>
              </div>
              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label>Email</Label>
                <div className="text-sm leading-6 text-slate-700">{c?.email || ""}</div>
              </div>
            </>
          ) : isBusiness ? (
            <Input
              placeholder="Business Name"
              value={businessName}
              onChange={(e) => { setBusinessName(e.target.value); setNameError(""); }}
            />
          ) : (
            <div className="flex gap-x-3">
              <Input
                placeholder="First Name"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setNameError(""); }}
              />
              <Input
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setNameError(""); }}
              />
            </div>
          )}
        </div>
        {needsName && (
          editingName ? (
            <Button type="button" onClick={handleSave}>Save</Button>
          ) : (
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => { setEditingName(true); setNameError(""); }}
            >
              <Edit className="w-4 h-4" />
            </Button>
          )
        )}
      </div>
      {nameError && (
        <p className="text-red-500 text-sm font-medium col-span-4 mt-1">{nameError}</p>
      )}
    </div>
  );
}

function AccountFields({ selectedAccount, register, control, errors }) {
  if (selectedAccount) {
    return (
      <>
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label>Bank</Label>
          <div className="text-sm text-slate-700">{selectedAccount.bank_name}</div>
        </div>
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label>Account type</Label>
          <div className="text-sm text-slate-700">{selectedAccount.account_type}</div>
        </div>
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label>Routing #</Label>
          <div className="text-sm text-slate-700">•••••{selectedAccount.routing_num_last_4}</div>
        </div>
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label>Account #</Label>
          <div className="text-sm text-slate-700">•••••{selectedAccount.account_num_last_4}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="grid w-full max-w-sm items-center gap-1.5">
        <Label>Account type</Label>
        <Controller
          control={control}
          name="accountType"
          rules={{ required: "Account type is required" }}
          render={({ field, fieldState }) => (
            <Select
              defaultValue={field.value}
              onValueChange={field.onChange}
              aria-invalid={fieldState.error ? "true" : "false"}
            >
              <SelectTrigger>
                <SelectValue placeholder="Account type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="checking">Individual Checking</SelectItem>
                  <SelectItem value="savings">Individual Savings</SelectItem>
                  <SelectItem value="business_checking">Business Checking</SelectItem>
                  <SelectItem value="business_savings">Business Savings</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        {errors.accountType && (
          <p role="alert" className="text-sm text-red-600">{errors.accountType.message}</p>
        )}
      </div>
      <div className="grid w-full max-w-sm items-center gap-1.5">
        <Label>Routing #</Label>
        <Input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={9}
          aria-invalid={errors.routing ? "true" : "false"}
          {...register("routing", routingValidation)}
        />
        {errors.routing && (
          <p role="alert" className="text-sm text-red-600">{errors.routing.message}</p>
        )}
      </div>
      <div className="grid w-full max-w-sm items-center gap-1.5">
        <Label>Account #</Label>
        <Input
          type="text"
          inputMode="numeric"
          aria-invalid={errors.account ? "true" : "false"}
          {...register("account", accountValidation)}
        />
        {errors.account && (
          <p role="alert" className="text-sm text-red-600">{errors.account.message}</p>
        )}
      </div>
    </>
  );
}

function AddAccountModal({
  open,
  onOpenChange,
  contactId,
  selectedAccount,
  selectedContact,
  setSelectedAccount,
  setPaymentType,
  balance,
}) {
  const history = useHistory();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId = userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const queryClient = useQueryClient();

  const amountValidation = {
    required: "Amount is required",
    validate: {
      isPositive: (value) => parseFloat(value) > 0 || "Amount must be greater than 0",
      balanceSufficient: (value) =>
        balance?.available_balance == null ||
        parseFloat(value) <= balance.available_balance ||
        "Insufficient balance",
    },
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    reset,
    watch,
    control,
  } = useForm({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      accountType: "checking",
      account: "",
      amount: "",
      routing: "",
    },
  });

  const [confirm, setConfirm] = useState(false);
  const [description, setDescription] = useState("");
  const [newAccount, setNewAccount] = useState(null);
  const [submittedAmount, setSubmittedAmount] = useState("");

  const { data: contact } = useContact(contactId, { enabled: !!contactId });
  const watchedAmount = watch("amount");
  const accountType = watch("accountType");

  const nameOnAccount = useMemo(() => {
    const c = contact?.contact_info;
    if (["business_checking", "business_savings"].includes(accountType)) {
      return c?.business_dba || c?.business_name || "";
    }
    return `${c?.first_name || ""} ${c?.last_name || ""}`.trim();
  }, [contact, accountType]);

  useEffect(() => {
    if (open) {
      reset({ accountType: "checking", account: "", amount: "", routing: "" });
      setNewAccount(null);
    }
  }, [open, reset]);

  const activeAccount = newAccount || selectedAccount;

  const buildDescription = (account, amount) =>
    `The following payment will be made to ${nameOnAccount}:\n\n` +
    `Bank Name: ${account.bank_name}\n` +
    `Routing #: ****${account.routing_num_last_4}\n` +
    `Account #: ****${account.account_num_last_4}\n` +
    `Amount: $${amount}\n\n` +
    `Estimated availability of funds will be 2-3 business days.`;

  useEffect(() => {
    const { amount } = getValues();
    if (!contact || !amount || !activeAccount) return;
    setDescription(buildDescription(activeAccount, amount));
  }, [contact, watchedAmount, activeAccount, nameOnAccount]);

  return (
    <>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        onConfirm={async () => {
          if (!activeAccount) {
            toast.error("Please select an account or add a new one");
            return;
          }
          try {
            await transferMoney(userId, {
              counter_party_id: activeAccount.counter_party_id,
              amount: parseFloat(submittedAmount).toFixed(2),
              third_party_id: contact.contact_id,
            });
            queryClient.invalidateQueries(["platform", userId, "balance"]);
            toast.success("Your payment has been submitted.");
            history.push("/transactions");
          } catch (e) {
            setConfirm(false);
            toast.error(
              e?.response?.data?.message || e?.message || "Payment failed. Please try again."
            );
          }
        }}
        title="Confirm your payment"
        description={description}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <form
            onSubmit={handleSubmit(async () => {
              if (!selectedAccount) {
                const c = contact?.contact_info;
                const isBusiness = ["business_checking", "business_savings"].includes(
                  accountType
                );
                if (isBusiness && !c?.business_name && !c?.business_dba) {
                  toast.error(
                    "Business name is required for business transfer. Please edit the contact info."
                  );
                  return;
                }
                if (!isBusiness && (!c?.first_name || !c?.last_name)) {
                  toast.error(
                    "First and Last Names are required for individual transfer. Please edit the contact info."
                  );
                  return;
                }
                try {
                  const fullName = `${contact?.contact_info?.first_name} ${contact?.contact_info?.last_name}`;
                  const res = await saveBankInfo({
                    userId: userId,
                    contactId: contact.contact_id,
                    nameOnAccount,
                    values: getValues(),
                  });
                  if (res.error) {
                    toast.error(`Error with ${res?.error?.field} number`);
                    return;
                  }
                  const { amount } = getValues();
                  setSubmittedAmount(amount);
                  setDescription(buildDescription(res.bank_info, amount));
                  setNewAccount(res.bank_info);
                  setPaymentType("pay_someone");
                  setConfirm(true);
                  onOpenChange(false);
                } catch (e) {
                  reset();
                  toast.error(
                    e?.response?.data?.message || e?.message || "Failed to save bank info."
                  );
                }
              } else {
                const { amount } = getValues();
                setSubmittedAmount(amount);
                setDescription(buildDescription(selectedAccount, amount));
                setPaymentType("pay_someone");
                setConfirm(true);
                onOpenChange(false);
              }
            })}
          >
            <DialogHeader>
              <DialogTitle className="text-xl">Pay someone from Your Pocketbook</DialogTitle>
            </DialogHeader>
            {!contact ? (
              <div className="flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4 pt-4">
                <ContactInfo contact={contact} accountType={accountType || "checking"} />
                <AccountFields
                  selectedAccount={selectedAccount}
                  register={register}
                  control={control}
                  errors={errors}
                />
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label>Amount</Label>
                  <div className="flex flex-row justify-center h-full gap-1 items-center">
                    <div>$</div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      aria-invalid={errors.amount ? "true" : "false"}
                      {...register("amount", amountValidation)}
                    />
                  </div>
                  {errors.amount && (
                    <p role="alert" className="text-sm text-red-600">
                      {errors.amount.message}
                    </p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                className="w-1/2"
                type="button"
                onClick={() => { reset(); onOpenChange(false); }}
              >
                Cancel
              </Button>
              <Button className="w-1/2" type="submit">
                Pay
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AddAccountModal;
